import { getConfig } from 'config/config';
import { BingAddress } from '../types';

type AzureMapsAddress = {
  streetNumber?: string;
  streetName?: string;
  municipality?: string;
  municipalitySubdivision?: string;
  countrySecondarySubdivision?: string;
  countrySubdivisionCode?: string;
  countrySubdivisionName?: string;
  postalCode?: string;
  countryCode?: string;
  country?: string;
  freeformAddress?: string;
};

type AzureMapsResult = {
  address?: AzureMapsAddress;
  position?: { lat: number; lon: number };
};

type AzureMapsResponse = {
  results?: AzureMapsResult[];
};

const azureMapsKey = getConfig('REACT_APP_AZURE_MAPS_KEY');
const requestUrlBuilder = (input: string) =>
  `https://atlas.microsoft.com/search/address/json?api-version=1.0&subscription-key=${azureMapsKey}&limit=7&query=${encodeURIComponent(input)}`;

function mapAzureAddressToBing(azure: AzureMapsAddress): BingAddress {
  const streetPart = [azure.streetNumber, azure.streetName].filter(Boolean).join(' ');
  return {
    countryRegion: azure.country ?? azure.countryCode ?? '',
    locality: azure.municipality ?? azure.municipalitySubdivision ?? '',
    adminDistrict: azure.countrySubdivisionName ?? azure.countrySubdivisionCode ?? '',
    adminDistrict2: azure.countrySecondarySubdivision ?? '',
    postalCode: azure.postalCode ?? '',
    addressLine: streetPart || (azure.freeformAddress ?? ''),
    formattedAddress: azure.freeformAddress ?? '',
  };
}

const queryAddresses = async (
  potentialAddress: string
): Promise<(BingAddress & { geocodePoints?: Array<{ coordinates: [number, number] }> })[]> => {
  const addresses: (BingAddress & { geocodePoints?: Array<{ coordinates: [number, number] }> })[] = [];

  if (!potentialAddress?.trim() || !azureMapsKey) return addresses;

  try {
    const response = await fetch(requestUrlBuilder(potentialAddress.trim()));
    const json = (await response.json()) as AzureMapsResponse;

    if (json?.results?.length) {
      return json.results.map((result) => {
        const bingAddress = result.address
          ? mapAzureAddressToBing(result.address)
          : ({
              countryRegion: '',
              locality: '',
              adminDistrict: '',
              adminDistrict2: '',
              postalCode: '',
              addressLine: '',
              formattedAddress: '',
            } as BingAddress);

        const geocodePoints =
          result.position != null
            ? [{ coordinates: [result.position.lat, result.position.lon] as [number, number] }]
            : undefined;

        return { ...bingAddress, geocodePoints };
      });
    }
  } catch {
    // Return empty on network error or parse failure
  }

  return addresses;
};

export const createGoogleMapsLink = (address: string): string => {
  const encodedAddress = encodeURIComponent(address);
  return `https://maps.google.com?q=${encodedAddress}`;
};

export default queryAddresses;
